import type { EditorSelection, Value } from '@platejs/slate';
import { Plate, PlateContent, usePlateEditor } from 'platejs/react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { fetchWithDefaults } from '@/lib/http/client';
import { type ResolvedSlot, resolveSlots } from '@/lib/scheduling/slotResolve';
import { cx } from '@/utils/cx';

import { PLATE_CONTENT_CLASS } from './constants';
import { createEmailEditorPlugins } from './email-editor-plugins';
import { EmailEditorToolbar } from './EmailEditorToolbar';
import type { ImageSize } from './ImageElement';
import { ImageSizeMenu } from './ImageSizeMenu';
import { parseHtmlToPlateValue } from './parse-html-to-plate-value';
import { serializePlateToHtml } from './plateEmailSerializer';
import { SignatureElement } from './SignatureElement';
import type { PlateEmailEditorHandle, PlateEmailEditorProps } from './types';
import { useImageUpload } from './use-image-upload';

// Single removal mechanism for both the in-block hover button and the
// imperative `removeSignature()` handle. If `node` is given, its path is
// resolved by object identity (the hover button already has the specific
// node it's attached to); otherwise the first `signature` node in the
// document is located by a type scan (the imperative handle has no node
// reference — the preview modal calls it generically). Either way, removal
// goes through the same `tf.removeNodes`, so both paths produce identical
// Plate transforms and drive the same onChange → 'user_removed' recording.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate editor/node instances not typed in base TElement
function removeSignatureNode(liveEditor: any, node?: any): boolean {
  let path;
  if (node) {
    path = liveEditor.api.findPath(node);
  } else {
    const entries = liveEditor.api.nodes({
      at: [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (n: any) => n.type === 'signature',
    });
    const first = entries ? Array.from(entries)[0] : undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- NodeEntry tuple not exported from platejs
    path = first ? (first as any)[1] : undefined;
  }
  if (!path) {
    return false;
  }
  liveEditor.tf.removeNodes({ at: path });
  return true;
}

const PlateEmailEditor = forwardRef<
  PlateEmailEditorHandle,
  PlateEmailEditorProps
>(function PlateEmailEditor(
  {
    value,
    onChange,
    placeholder = 'Enter your email content',
    readOnly = false,
    orgId,
    onAttachFile,
    isAttaching,
    toolbarTrailingContent,
    fillHeight = false,
    resolvedSlots,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastEmittedHtmlRef = useRef<string>(value || '');
  const isProgrammaticMutationRef = useRef<boolean>(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  // Slot ids with an in-flight resolve request. The resolution effect reruns on
  // every plateValue change (i.e. every keystroke); until the async resolve
  // returns and stamps resolutionAttempted, the pending-node predicate still
  // matches, so without this guard each keystroke would fire a duplicate
  // resolve for the same ids.
  const inFlightSlotIdsRef = useRef<Set<string>>(new Set());
  // Bumped by `replaceContent` so the slot-resolution and image-URL effects —
  // which otherwise re-run only when `plateValue` (derived from the controlled
  // `value` prop) changes — also re-run after an imperative `editor.tf.setValue`.
  // `replaceContent` mutates editor content directly WITHOUT changing `value`,
  // so `plateValue` is unchanged and neither effect would otherwise re-fire;
  // a <meeting-slot> chip pushed in via `replaceContent` would then stay on the
  // loading ellipsis because the resolution scan never runs. Bumping this epoch
  // (a dep of both effects) forces one scan against the replaced content.
  const [contentEpoch, setContentEpoch] = useState(0);
  const [sizeMenuOpen, setSizeMenuOpen] = useState(false);
  const [sizeMenuAnchor, setSizeMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate element with custom image properties (width, fileId) not typed in base TElement
  const [selectedImageElement, setSelectedImageElement] = useState<any>(null);
  const attachFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    lastEmittedHtmlRef.current = value || '';
  }, [value]);
  // Slot chip labels render in the sender's profile timezone (the backend
  // always resolves one — never the browser's), matching the server-side
  // slot-text render in the sent email (rendering.py). While the profile is
  // still loading, useDisplayTimezone returns the temporary 'UTC' fallback; the
  // slot-resolution effect below defers until loading completes so a chip is
  // never labeled in UTC and then left stale.
  const displayTimezone = useDisplayTimezone();
  const { data: profileData, isLoading: isProfileLoading } =
    useUserProfileData();
  // The backend always resolves a real IANA zone, so the PRESENCE of the field
  // — not the 'UTC' value useDisplayTimezone falls back to during loading AND
  // on a fetch error — is what tells us a chip can be safely labeled. Gate slot
  // resolution on it so a failed (not just in-flight) profile fetch never stamps
  // a chip in the UTC fallback; the effect retries once the field arrives.
  const hasResolvedProfileTimezone = !!profileData?.user?.timezone;
  // A ref mirror of the resolved display timezone so an in-flight slot
  // resolution stamps the CURRENT timezone when it completes, not the one its
  // effect captured when it started. Without this, a profile-timezone change
  // while `resolveSlots` is in flight would label the chip (and its `labelTz`)
  // in the stale zone — and because the relabel effect does not re-run merely
  // because `startIso` was set, the chip would stay stale.
  const displayTimezoneRef = useRef(displayTimezone);
  displayTimezoneRef.current = displayTimezone;
  const plateValue: Value = React.useMemo(
    () => parseHtmlToPlateValue(value),
    [value]
  );
  const onShowSizeMenu = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate element with custom image properties not typed in base TElement
    (anchorEl: HTMLElement, plateElement: any) => {
      setSizeMenuAnchor(anchorEl);
      setSelectedImageElement(plateElement);
      setSizeMenuOpen(true);
    },
    []
  );
  const imageUpload = useImageUpload({ orgId, readOnly, onShowSizeMenu });
  // Same editor-via-ref shape as useImageUpload's editorRef: the renderer
  // callback is built BEFORE usePlateEditor runs (the plugin array needs it),
  // so it reaches the editor through a ref instead of closing over the const.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate editor instance not typed for ref storage
  const editorForSignatureRemovalRef = useRef<any>(null);
  // A ref mirror of `readOnly` so the (stable) removal callback and imperative
  // handle can consult the CURRENT lock state at call time. The renderer and
  // plugin array below must stay reference-stable — usePlateEditor captures the
  // plugin array at mount and does not rebuild the editor when it changes, so a
  // `readOnly`-dependent renderer would leave the mounted editor wired to the
  // lock state from mount time (an initially-editable editor could still remove
  // while locked; an initially-locked one could never remove after unlock). The
  // live gate lives here (removal callbacks) and in SignatureElement (which
  // reads `useEditorReadOnly()` to hide the control), never in the renderer.
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  // Mirrors handleDeleteImage below: resolve the node's current path by object
  // identity, then a plain removeNodes. Removal flows through Plate's onChange,
  // so the owning surface's content handler sees the marker disappear and
  // records `signature_disposition: 'user_removed'` (reconcileSignatureDisposition).
  const handleRemoveSignature = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate signature node not typed in base TElement
    (node: any) => {
      // Never mutate a locked editor: the control is hidden while read-only, but
      // guard the callback too so a race (or the imperative path) can't remove.
      if (readOnlyRef.current) {
        return;
      }
      const liveEditor = editorForSignatureRemovalRef.current;
      if (!liveEditor) {
        return;
      }
      removeSignatureNode(liveEditor, node);
    },
    []
  );
  // Reference-stable: `onRemove` is the stable `handleRemoveSignature`, so the
  // renderer identity never changes on a `readOnly` flip. SignatureElement
  // decides whether to SHOW the control from the live `useEditorReadOnly()`.
  const SignatureElementWithHandlers = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Plate element props not typed in base TElement
    (props: any) =>
      React.createElement(SignatureElement, {
        ...props,
        onRemove: handleRemoveSignature,
      }),
    [handleRemoveSignature]
  );
  const plugins = React.useMemo(
    () =>
      createEmailEditorPlugins(
        imageUpload.ImageElementWithHandlers,
        SignatureElementWithHandlers
      ),
    [imageUpload.ImageElementWithHandlers, SignatureElementWithHandlers]
  );
  const editor = usePlateEditor({ plugins, value: plateValue });
  imageUpload.editorRef.current = editor;
  editorForSignatureRemovalRef.current = editor;

  // ── Focus survival across a read-only flip ────────────────────────────────
  //
  // The editor is put into read-only mode for the whole duration of an agent
  // chat turn (`isProcessingChat` → `editable={!isProcessing}` → `readOnly`).
  // Read-only sets `contentEditable=false` on the Plate content, and the
  // browser drops focus from a non-editable element — so a user who was
  // mid-sentence when the turn started silently lost their cursor, and nothing
  // gave it back when the turn ended.
  //
  // Capture focus + caret on the way in, restore them on the way out. We only
  // ever restore focus we actually held, so this can never steal focus from
  // wherever the user moved on to while the editor was locked.
  const wasFocusedWhenLockedRef = useRef(false);
  const caretWhenLockedRef = useRef<EditorSelection>(null);

  // Captured during render, NOT in an effect: effects run after the DOM has
  // been committed, by which point `contentEditable=false` has already blurred
  // the editor and `document.activeElement` is `<body>`. On the render that
  // flips `readOnly` the DOM still holds the pre-lock state, so this is the
  // last moment the caret and focus are observable. (Same adjust-state-on-
  // prop-change shape as ProposalCenter's view snapshot.)
  const [prevReadOnly, setPrevReadOnly] = useState(readOnly);
  if (prevReadOnly !== readOnly) {
    if (readOnly) {
      wasFocusedWhenLockedRef.current =
        containerRef.current?.contains(document.activeElement) ?? false;
      caretWhenLockedRef.current = editor.selection;
    }
    setPrevReadOnly(readOnly);
  }

  // The lock can span a full agent turn, during which the user may click away
  // to some other interactive element entirely. That later focus change isn't
  // observable at lock time, so watch for it live: any focusin that lands
  // outside our container while locked means the user moved on, and the
  // restore effect below must not steal focus back from them on unlock.
  useEffect(() => {
    if (!readOnly) {
      return;
    }
    const handleFocusIn = ({ target }: FocusEvent) => {
      if (target instanceof Node && !containerRef.current?.contains(target)) {
        wasFocusedWhenLockedRef.current = false;
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [readOnly]);

  useEffect(() => {
    if (readOnly || !wasFocusedWhenLockedRef.current) {
      return;
    }
    wasFocusedWhenLockedRef.current = false;
    const caret = caretWhenLockedRef.current;
    caretWhenLockedRef.current = null;
    let usedCaretFallback = false;
    try {
      editor.tf.focus();
      // The agent usually replaces the body while we are locked, so the saved
      // caret can point at a path that no longer exists in the new content.
      // Try it, then fall back to the end of the document — never leave the
      // editor focused with no selection.
      if (caret) {
        try {
          editor.tf.select(caret);
        } catch {
          // Caret no longer resolves against the replaced content.
          usedCaretFallback = true;
          editor.tf.select(editor.api.end([]));
        }
      } else {
        usedCaretFallback = true;
        editor.tf.select(editor.api.end([]));
      }
    } catch (error) {
      console.error(
        '[PlateEmailEditor] Failed to restore focus after read-only:',
        { readOnly, usedCaretFallback, error }
      );
    }
  }, [readOnly, editor]);

  // In-place content replacement — the only sanctioned way to push external
  // content into a mounted editor. Never remount (key-bump) to sync content:
  // remounting destroys DOM, focus, scroll position, and cursor.
  useImperativeHandle(
    ref,
    () => ({
      replaceContent: (html: string) => {
        const wasFocused =
          containerRef.current?.contains(document.activeElement) ?? false;
        // Suppress onChange during the programmatic replacement — external
        // content must never round-trip back out as if the user typed it.
        isProgrammaticMutationRef.current = true;
        try {
          editor.tf.setValue(parseHtmlToPlateValue(html));
        } finally {
          isProgrammaticMutationRef.current = false;
        }
        lastEmittedHtmlRef.current = html;
        // Undo must not resurrect pre-replacement content (matches the
        // semantics of the old remount path, which reset history).
        editor.history.undos = [];
        editor.history.redos = [];
        if (wasFocused) {
          // Keep the cursor valid without stealing focus or scrolling.
          editor.tf.select(editor.api.end([]));
        }
        // Re-run the slot-resolution / image-URL effects against the freshly
        // replaced content — `plateValue` did not change, so the epoch is the
        // only signal that new chips/images may now be present.
        setContentEpoch(epoch => epoch + 1);
      },
      removeSignature: () => {
        // Never mutate a locked editor: a read-only removeNodes would be dropped
        // by handlePlateChange (its `if (readOnly) return`), so onChange never
        // fires and the caller records neither the body change nor
        // 'user_removed'. Return false instead so the caller applies its
        // fallback (setting 'user_removed' directly). Mirrors the live gate on
        // the in-block remove button (handleRemoveSignature).
        if (readOnlyRef.current) {
          return false;
        }
        // Read the live editor via the same ref the hover path uses, not the
        // `editor` closure variable — guards against acting on a stale
        // reference the way handleRemoveSignature already does.
        const liveEditor = editorForSignatureRemovalRef.current;
        if (!liveEditor) {
          return false;
        }
        // The SAME helper as the in-block remove button — one removal
        // mechanism everywhere. Goes through Plate's normal transform so
        // onChange fires and the surface records `user_removed`.
        return removeSignatureNode(liveEditor);
      },
    }),
    [editor]
  );

  useEffect(() => {
    if (!orgId) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate NodeEntry/match predicate types not exported from platejs
    const pendingNodes: Array<[any, any]> = [];
    try {
      const nodesIterator = editor.api.nodes({
        at: [],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (n: any) =>
          n.type === 'img' && n.pendingUrlResolution && n.fileId,
      });
      if (
        nodesIterator &&
        typeof nodesIterator[Symbol.iterator] === 'function'
      ) {
        for (const entry of nodesIterator) {
          pendingNodes.push(entry as [any, any]); // eslint-disable-line @typescript-eslint/no-explicit-any
        }
      }
    } catch (error) {
      console.error('Error finding pending image nodes:', error);
      return;
    }

    if (pendingNodes.length === 0) {
      return;
    }

    const setProgrammatic = (
      patch: Record<string, unknown>,
      fileId: number
    ) => {
      try {
        isProgrammaticMutationRef.current = true;
        editor.tf.setNodes(patch, {
          at: [],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          match: (n: any) =>
            n.fileId === fileId && n.pendingUrlResolution === true,
        });
      } finally {
        isProgrammaticMutationRef.current = false;
      }
    };
    const clearPending = (fileId: number) =>
      setProgrammatic({ pendingUrlResolution: undefined }, fileId);

    const setResolutionError = (fileId: number, status?: number) => {
      console.warn(
        `[PlateEmailEditor] Image unavailable for file ${fileId}${
          status ? ` (HTTP ${status})` : ''
        }`
      );
      setProgrammatic(
        {
          pendingUrlResolution: undefined,
          urlResolutionError: 'Image unavailable',
        },
        fileId
      );
    };

    const resolveUrls = async () => {
      for (const [node] of pendingNodes) {
        try {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/files/${node.fileId}/`
          );
          if (response.ok) {
            const fileData = await response.json();
            let resolvedUrl = fileData.file_url || fileData.download_url;
            // Fix Docker internal hostname - replace 'web:8000' with current origin
            if (resolvedUrl && resolvedUrl.includes('://web:')) {
              resolvedUrl = resolvedUrl.replace(
                /^https?:\/\/web:\d+/,
                window.location.origin
              );
            }
            if (resolvedUrl) {
              setProgrammatic(
                { url: resolvedUrl, pendingUrlResolution: undefined },
                node.fileId
              );
            } else {
              console.error(
                `[PlateEmailEditor] No URL returned for file ${node.fileId}`
              );
              clearPending(node.fileId);
            }
          } else {
            setResolutionError(node.fileId, response.status);
          }
        } catch {
          setResolutionError(node.fileId);
        }
      }

      // Emit resolved state to parent so resolved URLs propagate without requiring a manual edit
      const htmlContent = serializePlateToHtml(editor.children as Value);
      if (htmlContent !== lastEmittedHtmlRef.current) {
        lastEmittedHtmlRef.current = htmlContent;
        onChangeRef.current(htmlContent);
      }
    };

    resolveUrls();
  }, [editor, orgId, plateValue, contentEpoch]);

  // ── Slot chip label resolution ───────────────────────────────────────────
  //
  // A `slot` node only carries a `slotId` reference until its start time is
  // resolved (draft re-open never persists the label — see
  // plateEmailSerializer's slot serialization, which emits only `slotId`).
  // Once a chip has been attempted (!resolutionAttempted is false) it is
  // skipped permanently — resolution is once-and-done, not repeated. The
  // relabel effect below is MORE load-bearing after this change: it is the
  // only path that updates labels on already-resolved chips (e.g. on a
  // profile-timezone change). Do not remove it as TTL residue.
  useEffect(() => {
    if (!orgId) {
      return;
    }
    // Defer until the profile timezone is actually resolved. displayTimezone is
    // the 'UTC' fallback during the profile-fetch window AND when the fetch
    // errors; resolving a chip now would stamp its label in UTC and leave it
    // stale. Gate on the resolved field (not merely !isProfileLoading, which
    // also goes false on error) so a failed fetch never labels a chip; the
    // effect re-runs once the field arrives (hasResolvedProfileTimezone +
    // displayTimezone are deps).
    if (isProfileLoading || !hasResolvedProfileTimezone) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate NodeEntry/match predicate types not exported from platejs
    const dueSlotNodes: Array<[any, any]> = [];
    try {
      const nodesIterator = editor.api.nodes({
        at: [],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (n: any) =>
          n.type === 'slot' &&
          !!n.slotId &&
          !n.resolutionAttempted &&
          !n.startIso,
      });
      if (
        nodesIterator &&
        typeof nodesIterator[Symbol.iterator] === 'function'
      ) {
        for (const entry of nodesIterator) {
          // Defensive re-check: don't trust `match` alone to have filtered —
          // guards a node-query host that ignores the predicate.
          const [node] = entry as [any, any]; // eslint-disable-line @typescript-eslint/no-explicit-any
          if (node?.type === 'slot' && node.slotId) {
            dueSlotNodes.push(entry as [any, any]); // eslint-disable-line @typescript-eslint/no-explicit-any
          }
        }
      }
    } catch (error) {
      console.error('Error finding due slot nodes:', error);
      return;
    }

    if (dueSlotNodes.length === 0) {
      return;
    }

    const slotIds = [
      ...new Set(dueSlotNodes.map(([node]) => node.slotId as string)),
    ].filter(id => !inFlightSlotIdsRef.current.has(id));

    if (slotIds.length === 0) {
      return;
    }

    const setProgrammaticSlot = (
      patch: Record<string, unknown>,
      slotId: string
    ) => {
      try {
        isProgrammaticMutationRef.current = true;
        editor.tf.setNodes(patch, {
          at: [],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          match: (n: any) => n.type === 'slot' && n.slotId === slotId,
        });
      } finally {
        isProgrammaticMutationRef.current = false;
      }
    };

    // Ids the caller already resolved (e.g. action.enriched_data.resolved_slots)
    // are stamped directly from that map — no resolve-endpoint round-trip.
    // `label` is SERVER-rendered (in the requesting user's profile timezone)
    // and displayed verbatim: render_slot_time on the backend is the single
    // source of the slot format, never re-implemented here.
    const resolvedSlotsMap = resolvedSlots ?? {};
    const preResolvedIds = slotIds.filter(id =>
      Object.hasOwn(resolvedSlotsMap, id)
    );
    const idsToFetch = slotIds.filter(
      id => !Object.hasOwn(resolvedSlotsMap, id)
    );

    preResolvedIds.forEach(id => {
      const slot = resolvedSlotsMap[id];
      setProgrammaticSlot(
        {
          startIso: slot.start,
          label: slot.label,
          labelTz: displayTimezoneRef.current,
          resolutionAttempted: true,
        },
        id
      );
    });

    if (idsToFetch.length === 0) {
      return;
    }

    idsToFetch.forEach(id => inFlightSlotIdsRef.current.add(id));

    const resolveDueSlots = async () => {
      const resolvedIds = new Set<string>();
      // Track only the ids we actually issued a request for. A failure on one chunk
      // must NOT mark ids in later, never-sent chunks as failed — those stay pending
      // so a subsequent effect run retries them instead of stranding them on
      // "Unable to load time" permanently after a transient error.
      const attemptedIds = new Set<string>();
      // Apply each chunk's slots the moment that chunk resolves — NOT after the
      // whole batch settles. `resolveSlots` rejects the entire promise (discarding
      // its accumulated return value) as soon as any chunk fails, so applying only
      // after `await` would lose every earlier successful chunk. Applying per-chunk
      // means a later chunk's failure never un-resolves ids an earlier chunk already
      // resolved. `displayTimezoneRef.current` (not the captured `displayTimezone`)
      // ensures a mid-flight profile-timezone change labels the chip in the current
      // zone.
      const applyResolved = (slots: ResolvedSlot[]) => {
        for (const slot of slots) {
          resolvedIds.add(slot.id);
          setProgrammaticSlot(
            {
              startIso: slot.start,
              // Server-rendered label, displayed verbatim (single-source
              // format — see slotResolve.ts).
              label: slot.label,
              // Remember which timezone the label was rendered in so a later
              // profile-timezone change can relabel this chip (see the
              // relabel effect below).
              labelTz: displayTimezoneRef.current,
              resolutionAttempted: true,
            },
            slot.id
          );
        }
      };
      try {
        await resolveSlots(
          orgId,
          idsToFetch,
          chunk => chunk.forEach(id => attemptedIds.add(id)),
          applyResolved
        );
        // The endpoint silently omits ids it can no longer resolve at all
        // (deleted, or never existed) — mark them failed as well as
        // attempted so the chip renders "Unable to load time" instead of
        // sitting on the loading ellipsis forever.
        for (const slotId of idsToFetch) {
          if (!resolvedIds.has(slotId)) {
            setProgrammaticSlot(
              { resolutionAttempted: true, resolutionFailed: true },
              slotId
            );
          }
        }
      } catch (error) {
        console.error('[PlateEmailEditor] Failed to resolve slots:', error);
        // Only ids we attempted AND did not already resolve are marked failed.
        // Earlier chunks were already applied via `applyResolved`, so a later
        // chunk's rejection leaves their resolved labels intact.
        for (const slotId of attemptedIds) {
          if (resolvedIds.has(slotId)) {
            continue;
          }
          setProgrammaticSlot(
            { resolutionAttempted: true, resolutionFailed: true },
            slotId
          );
        }
      } finally {
        idsToFetch.forEach(id => inFlightSlotIdsRef.current.delete(id));
      }
    };

    resolveDueSlots();
    // `displayTimezone` is intentionally NOT a dep: the resolution apply reads
    // `displayTimezoneRef.current` (always current), and already-resolved chips
    // are relabeled by the separate relabel effect below on a tz change. Adding
    // it here would only re-scan for due chips on every tz change with no new
    // work to do. `contentEpoch` IS a dep so a `replaceContent` (which mutates
    // the editor without changing `plateValue`) still triggers a resolution scan
    // for any newly-inserted chips.
  }, [
    editor,
    orgId,
    plateValue,
    contentEpoch,
    isProfileLoading,
    hasResolvedProfileTimezone,
    resolvedSlots,
  ]);

  // ── Relabel resolved chips on a profile-timezone change ──────────────────
  //
  // The resolution effect above only matches chips that have never been
  // attempted (!resolutionAttempted && !startIso), so once a chip is resolved
  // it is permanently skipped by that effect. A profile-timezone change must
  // still relabel those chips: this effect is the only path that does so.
  // Labels are SERVER-rendered (in the profile timezone — the single source of
  // the slot format), so a timezone change requires a resolve-endpoint
  // round-trip: by the time the profile change is visible here, the backend
  // renders labels in the new zone. On a failed refetch the chip keeps its
  // previous (old-zone) label — stale but correct-in-substance, and the next
  // tz change or reopen retries.
  useEffect(() => {
    if (!orgId || !hasResolvedProfileTimezone) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Slate NodeEntry types not exported from platejs
    const staleNodes: Array<[any, any]> = [];
    try {
      const nodesIterator = editor.api.nodes({
        at: [],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        match: (n: any) =>
          n.type === 'slot' &&
          !!n.slotId &&
          !!n.startIso &&
          n.labelTz !== displayTimezone,
      });
      if (
        nodesIterator &&
        typeof nodesIterator[Symbol.iterator] === 'function'
      ) {
        for (const entry of nodesIterator) {
          staleNodes.push(entry as [any, any]); // eslint-disable-line @typescript-eslint/no-explicit-any
        }
      }
    } catch (error) {
      console.error('Error finding chips to relabel:', error);
      return;
    }

    if (staleNodes.length === 0) {
      return;
    }

    const staleIds = Array.from(
      new Set(staleNodes.map(([node]) => node.slotId as string))
    );
    let cancelled = false;
    resolveSlots(orgId, staleIds)
      .then(slots => {
        if (cancelled) {
          return;
        }
        for (const slot of slots) {
          try {
            isProgrammaticMutationRef.current = true;
            editor.tf.setNodes(
              {
                label: slot.label,
                labelTz: displayTimezone,
              },
              {
                at: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                match: (n: any) => n.type === 'slot' && n.slotId === slot.id,
              }
            );
          } finally {
            isProgrammaticMutationRef.current = false;
          }
        }
      })
      .catch(error => {
        console.error('[PlateEmailEditor] Failed to relabel slots:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [editor, orgId, displayTimezone, hasResolvedProfileTimezone]);

  const handlePlateChange = useCallback(
    ({ value: newValue }: { value: Value }) => {
      if (readOnly) {
        return;
      }
      // Suppress onChange during programmatic mutations (e.g., image URL resolution)
      if (isProgrammaticMutationRef.current) {
        // Still update lastEmittedHtmlRef so subsequent emissions compare correctly
        lastEmittedHtmlRef.current = serializePlateToHtml(newValue);
        return;
      }
      const htmlContent = serializePlateToHtml(newValue);
      if (htmlContent !== lastEmittedHtmlRef.current) {
        lastEmittedHtmlRef.current = htmlContent;
        onChange(htmlContent);
      }
    },
    [readOnly, onChange]
  );

  const handleAttachFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList && fileList.length > 0 && onAttachFile) {
        onAttachFile(Array.from(fileList));
      }
      event.target.value = '';
    },
    [onAttachFile]
  );

  const handleAttachFileButtonClick = useCallback(() => {
    attachFileInputRef.current?.click();
  }, []);

  const handleSizeChange = useCallback(
    (size: ImageSize) => {
      if (!selectedImageElement) {
        return;
      }
      const path = editor.api.findPath(selectedImageElement);
      if (path) {
        editor.tf.setNodes({ width: size }, { at: path });
      }
    },
    [editor, selectedImageElement]
  );

  const handleDeleteImage = useCallback(() => {
    if (!selectedImageElement) {
      return;
    }
    const path = editor.api.findPath(selectedImageElement);
    if (path) {
      editor.tf.removeNodes({ at: path });
    }
  }, [editor, selectedImageElement]);

  const handleSizeMenuClose = useCallback(() => setSizeMenuOpen(false), []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!readOnly && orgId) {
        e.preventDefault();
      }
    },
    [readOnly, orgId]
  );

  // Content-hugging floors, used when the editor is NOT filling its container.
  const hugMinHeightClass = readOnly ? 'min-h-[100px]' : 'min-h-[200px]';
  // Fill mode: become the flex row that owns the remaining height so
  // PLATE_CONTENT_CLASS's `h-full` has something real to resolve against.
  const contentAreaClass = fillHeight
    ? 'flex-1 min-h-0 flex flex-col'
    : hugMinHeightClass;

  return (
    <div
      ref={containerRef}
      className={cx('relative', fillHeight && 'h-full flex flex-col')}
    >
      <input
        ref={imageUpload.fileInputRef}
        type='file'
        accept='image/*'
        onChange={imageUpload.handleFileInputChange}
        className='hidden'
      />
      {onAttachFile && (
        <input
          ref={attachFileInputRef}
          type='file'
          multiple
          className='hidden'
          tabIndex={-1}
          onChange={handleAttachFileChange}
        />
      )}
      <ImageSizeMenu
        isOpen={sizeMenuOpen}
        onClose={handleSizeMenuClose}
        onSizeChange={handleSizeChange}
        onDelete={handleDeleteImage}
        anchorElement={sizeMenuAnchor}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Custom image element width property not in base TElement
        currentSize={(selectedImageElement as any)?.width}
      />
      <Plate editor={editor} onChange={handlePlateChange}>
        <div
          className={cx('w-full relative transition-all', contentAreaClass)}
          onPaste={imageUpload.handlePaste}
          onDrop={imageUpload.handleDrop}
          onDragOver={handleDragOver}
        >
          {!readOnly && (
            <EmailEditorToolbar
              orgId={orgId}
              onImageButtonClick={imageUpload.handleImageButtonClick}
              onAttachFile={onAttachFile}
              isAttaching={isAttaching}
              onAttachFileButtonClick={handleAttachFileButtonClick}
              trailingContent={toolbarTrailingContent}
            />
          )}
          <PlateContent
            readOnly={readOnly}
            className={cx(
              PLATE_CONTENT_CLASS,
              // Claim the space left by the toolbar above, and own the scroll
              // so long emails scroll inside the typing area rather than
              // growing the panel.
              fillHeight && 'flex-1 min-h-0 overflow-y-auto'
            )}
            placeholder={placeholder}
          />
        </div>
      </Plate>
    </div>
  );
});

export default PlateEmailEditor;
